// Der Inhalt der Ingame-Hilfe — als **reine Funktionen, die HTML liefern**.
//
// Warum hier und nicht in `panels.ts`: die Hilfe ist der laengste Text des
// Spiels, und in `panels.ts` waere sie ungetesteter Code. Hier ist sie DOM-frei
// und laeuft damit in `vitest` (das ohne jsdom faehrt, siehe `tests/setup.ts`).
// Der Fortschritt kommt als **Parameter** herein, nicht als Store-Import —
// genau das macht jede Funktion ohne Attrappe aufrufbar.
//
// **Keine Zahl steht in dieser Datei** (Gate FG-31). Distanzen, Haerten,
// Ausbeuten, Radien und Dauern kommen aus `balancing()`; wer eine Zone
// verschiebt, veraendert die Hilfe mit, ohne sie anzufassen.
//
// **Keine erfundenen Icon-Namen** (Gate FG-25): die Hilfe zeichnet
// ausschliesslich `resIconSvg` mit Ressourcen aus der Tabelle. Ein
// hartkodierter Icon-Name wuerde still ein Schloss rendern, und FG-25 prueft
// nur die Datentabellen, nicht diesen Text.

import { distStr, escapeHtml, fmt, timeStr } from '../core/format';
import {
  balancing, build, colonyBuildings, goods, planets, projects, projectsOf, resource, zonesFor, zonesOfOre,
} from '../data/balancing';
import type { Build, Enemy, Quest, Resource, Zone } from '../data/types';
import { resIconSvg } from '../gfx/icons';
import { KEY_BINDINGS, TOUCH_HINTS } from './keys';

/** Attributname des Loeschknopfes — eine Zeichenkette, zwei Leser. */
export const WIPE_ATTR = 'data-help-wipe';

export interface HelpPage {
  id: string;
  title: string;
}

export const HELP_PAGES: readonly HelpPage[] = [
  { id: 'basics', title: 'Überblick' },
  { id: 'zones', title: 'Zonen' },
  { id: 'field', title: 'Feld' },
  { id: 'enemies', title: 'Gegner' },
  { id: 'progress', title: 'Fortschritt' },
  { id: 'coop', title: 'Zusammen' },
  { id: 'colony', title: 'Kolonie' },
  { id: 'controls', title: 'Steuerung' },
];

/** Was die Hilfe vom Spielstand wissen muss — bewusst wenig. */
export interface HelpProgress {
  /** Groesster je erreichter Abstand zur Basis (`store.stat('deepest')`). */
  deepest: number;
  /**
   * Die Aera des Spielstands (`store.save.era`).
   *
   * Sie steht hier und nicht als eigener Parameter jeder Seitenfunktion: die
   * Hilfe ist ein Nachschlagewerk **des laufenden Spielstands**, und ihre
   * Zahlen kommen alle aus derselben Frage „wo stehe ich". Eine Zonenliste
   * aus Akt I neben einem Feld aus Akt II waere kein Fehler, den irgendetwas
   * meldet — nur ein Text, der nicht stimmt.
   */
  era: number;
}

export interface ZoneOre {
  id: string;
  name: string;
  /** Anteil an der Erztabelle dieser Zone, 0…100. */
  share: number;
  icon: string;
}

export interface ZoneEnemy {
  name: string;
  minBuild: number;
  colossal: boolean;
}

export interface ZoneFacts {
  index: number;
  name: string;
  sub: string;
  desc: string;
  from: number;
  /** Ende des Rings — die naechste Zonengrenze, bei der letzten die Alphawand. */
  to: number;
  last: boolean;
  density: number;
  /** Name der ersten Zone dieser Ära — der Bezugspunkt der Vielfachen. */
  baseName: string;
  /** Vielfache gegenueber der ersten Zone — absolute Tabellenwerte sagen nichts. */
  hpMul: number;
  yieldMul: number;
  xpMul: number;
  ores: ZoneOre[];
  /** Arten, die **genau hier** zum ersten Mal auftreten (`minZone === index`). */
  newEnemies: ZoneEnemy[];
  newAnomalies: string[];
  /** Wie viele Arten hier ueberhaupt vorkommen (`minZone <= index`). */
  enemyKinds: number;
  anomalyKinds: number;
  colossus: boolean;
  probes: boolean;
  /** Reine Anzeige: war der Spieler schon so weit draussen? */
  reached: boolean;
}

// ── Kleine Bausteine ─────────────────────────────────────────────────────────

/** Deutsche Dezimalschreibung fuer die wenigen Bruchzahlen der Hilfe. */
function dec(n: number): string {
  return n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

/** „1 Gegnerart" statt „1 Gegnerarten" — der Zaehler steht neben dem Wort. */
function anzahl(n: number, einzahl: string, mehrzahl: string): string {
  return `${n} ${n === 1 ? einzahl : mehrzahl}`;
}

/**
 * Ein Vielfaches.
 *
 * Kleine Werte in deutscher Schreibung (`8,5`), grosse im Zahlenformat des
 * Spiels (`275.0K`) — sonst staende „8.5× Ausbeute" neben „Dichte 1,15" in
 * derselben Karte.
 */
function mul(n: number): string {
  return n < 1000 ? dec(n) : fmt(n);
}

/** Eine Kennzahlenzeile. */
function row(label: string, value: string): string {
  return `<div class="hrow"><span class="hkey">${escapeHtml(label)}</span><span class="hval">${value}</span></div>`;
}

function para(text: string): string {
  return `<p class="hp">${text}</p>`;
}

function head(text: string): string {
  return `<h3 class="perk-head">${escapeHtml(text)}</h3>`;
}

/** Die Gegnerarten **einer Ära** — dieselbe Bedingung wie im Feld (`_eligible`). */
function enemiesOf(era: number): Enemy[] {
  return balancing().enemies.filter((foe) => foe.minEra <= era);
}

/**
 * Die Ausbaukette **einer Ära**.
 *
 * Die Hilfe erklärt den Akt, in dem der Spieler steht. Alle Stufen beider
 * Ketten untereinander wären nach dem Sprung eine Liste, deren erste Hälfte er
 * längst kennt — und vor dem Sprung eine Vorschau auf ein Spiel, das er noch
 * nicht gesehen hat.
 */
function chainOf(era: number): Build[] {
  return balancing().builds.filter((entry) => entry.minEra === era);
}

/** Die Aufträge, die in dieser Ära überhaupt in der Liste stehen. */
function questsOf(era: number): Quest[] {
  return balancing().quests.filter((entry) => entry.minEra <= era);
}

/** Die Erze, die in den Zonen einer Ära wirklich fallen. */
function oresOf(era: number): string[] {
  const gesehen = new Set<string>();
  for (const zone of zonesFor(era)) for (const ore of zone.ores) gesehen.add(ore.res);
  return [...gesehen];
}

/**
 * Der Anteil eines Erzes an den Ziehungen einer Zone, in Prozent.
 *
 * `ores` ist eine **Gewichtstabelle**, kein Vorkommen-Flag: erst das Gewicht
 * gegen die Summe sagt, ob ein Erz dort die Regel oder die Ausnahme ist.
 */
function oreShare(zone: Zone, resId: string): number {
  const gesamt = zone.ores.reduce((sum, ore) => sum + ore.weight, 0);
  const eintrag = zone.ores.find((ore) => ore.res === resId);
  if (!eintrag || gesamt <= 0) return 0;
  return (eintrag.weight / gesamt) * 100;
}

/** Der Name der Zone an einem Index — fuer „ab Zone x"-Angaben. */
function zoneName(index: number, era: number): string {
  const zone = zonesFor(era)[index];
  return zone ? zone.name : '';
}

/** Eine Kostenkarte („4.0M Gold · 250K Kupfer") aus einer Ressourcenkarte. */
function costText(cost: Record<string, number>): string {
  return Object.entries(cost)
    .map(([id, amount]) => {
      const res = resource(id);
      return `${res ? resIconSvg(res, 14) : ''}${fmt(amount)} ${escapeHtml(res ? res.name : id)}`;
    })
    .join(' · ');
}

// ── Zonen ────────────────────────────────────────────────────────────────────

export function zoneFacts(index: number, deepest: number, era: number): ZoneFacts {
  const list = zonesFor(era);
  const zone = list[index];
  const data = balancing();
  const next = list[index + 1];

  return {
    index,
    name: zone.name,
    sub: zone.sub,
    desc: zone.desc,
    from: zone.from,
    to: next ? next.from : data.world.fieldRadius,
    last: !next,
    density: zone.density,
    baseName: list[0].name,
    hpMul: zone.hp / list[0].hp,
    yieldMul: zone.yield / list[0].yield,
    xpMul: zone.xp / list[0].xp,
    ores: zone.ores.map((ore) => {
      const res = resource(ore.res);
      return {
        id: ore.res,
        name: res ? res.name : ore.res,
        share: oreShare(zone, ore.res),
        icon: res ? resIconSvg(res, 14) : '',
      };
    }),
    // **Nach Ära gefiltert.** Die Hilfe ist ein Nachschlagewerk des laufenden
    // Spielstands: die Wächter der Ruinen im Randfeld anzukündigen wäre eine
    // Zone, in der etwas steht, das dort nie auftaucht.
    newEnemies: enemiesOf(era)
      .filter((foe) => foe.minZone === index)
      .map((foe) => ({ name: foe.name, minBuild: foe.minBuild, colossal: foe.colossal })),
    newAnomalies: data.anomalies.filter((kind) => kind.minZone === index).map((kind) => kind.name),
    enemyKinds: enemiesOf(era).filter((foe) => foe.minZone <= index).length,
    anomalyKinds: data.anomalies.filter((kind) => kind.minZone <= index).length,
    colossus: index >= data.colossus.minZone,
    probes: index >= data.probes.minZone,
    reached: deepest >= zone.from,
  };
}

export function zoneFactsAll(deepest: number, era: number): ZoneFacts[] {
  return zonesFor(era).map((_zone, index) => zoneFacts(index, deepest, era));
}

/** Ein Gegnername mit der Bedingung, unter der er ueberhaupt auftaucht. */
function enemyLabel(foe: ZoneEnemy): string {
  const name = escapeHtml(foe.name);
  if (foe.colossal) return `${name} — nur gemeinsam zu schaffen`;
  return foe.minBuild > 0
    ? `${name} (ab ${anzahl(foe.minBuild, 'Ausbaustufe', 'Ausbaustufen')})`
    : name;
}

/**
 * „Wo finde ich dieses Erz?" — der Tooltip der Erzleiste und der Filterchips.
 *
 * Ein Brocken trägt keine Beschriftung, und die Antwort stand bisher nur auf
 * der Feldseite dieser Hilfe: drei Klicks entfernt von der Leiste, an der man
 * die Frage hat. Sie steht hier und nicht im HUD, weil sie damit DOM-frei und
 * geprüft ist — und weil die Feldseite dieselbe Ableitung benutzt.
 *
 * Die **Entfernung** ist der eigentliche Gewinn gegenüber dem Zonennamen: „ab
 * 1.200 m" ist eine Handlungsanweisung, „Kupferschwarm" ist eine Vokabel.
 *
 * Kein Fund ist eine gültige Antwort: was der Konverter erzeugt, liegt in
 * keiner Zone — und die Leiste zeigt jedes Erz, das je gefallen **oder
 * entstanden** ist.
 */
export function oreTipHtml(res: Resource, era: number): string {
  const kopf = `<div class="tt-name">${resIconSvg(res, 14)} ${escapeHtml(res.name)}</div>`;
  const zonen = zonesOfOre(res.id, era);
  if (zonen.length === 0) {
    return kopf + '<div class="tt-desc">Fällt in keiner Zone — es entsteht im Konverter.</div>';
  }
  return kopf
    + `<div class="tt-desc">${res.gem ? 'Edelstein · ' : ''}fällt in:</div>`
    + zonen
      .map((zone) => `<div class="tt-eff">${escapeHtml(zone.name)} `
        + `${Math.round(oreShare(zone, res.id))}% · ab ${escapeHtml(distStr(zone.from))}</div>`)
      .join('');
}

export function zoneCardHtml(facts: ZoneFacts): string {
  const spanne = facts.last
    ? `ab ${distStr(facts.from)} bis zur Alphawand bei ${distStr(facts.to)}`
    : `${distStr(facts.from)} – ${distStr(facts.to)}`;

  const erz = facts.ores
    .map((ore) => `${ore.icon} ${escapeHtml(ore.name)} ${Math.round(ore.share)}%`)
    .join(' · ');

  const neu = [
    ...facts.newEnemies.map(enemyLabel),
    ...facts.newAnomalies.map((name) => escapeHtml(name)),
  ].join(' · ');

  const auch: string[] = [
    anzahl(facts.enemyKinds, 'Gegnerart', 'Gegnerarten'),
    anzahl(facts.anomalyKinds, 'Anomalieart', 'Anomaliearten'),
  ];
  if (facts.colossus) auch.push('Kolossbrocken');
  if (facts.probes) auch.push('verlassene Sonden');

  const brocken = `${mul(facts.hpMul)}× Härte · ${mul(facts.yieldMul)}× Ausbeute · ` +
    `${mul(facts.xpMul)}× Erfahrung`;

  return `<div class="zone ${facts.reached ? 'reached' : 'far'}">
      <div class="zone-head">
        <span class="zone-name">${escapeHtml(facts.name)}</span>
        <span class="zone-sub">${escapeHtml(facts.sub)}</span>
        <span class="zone-mark">${facts.reached ? '✓ erreicht' : 'noch nicht erreicht'}</span>
      </div>
      <div class="zone-span">${spanne}</div>
      ${para(escapeHtml(facts.desc))}
      ${row('Erz', erz)}
      ${row('Brocken', brocken)}
      ${row('Dichte', `${dec(facts.density)} — Brocken je Fläche, gemessen am ` +
        `${escapeHtml(facts.baseName)}`)}
      ${neu ? row('Neu hier', neu) : row('Neu hier', 'nichts — hier kommt keine Art dazu')}
      ${row('Außerdem', auch.join(' · '))}
    </div>`;
}

// ── Die einzelnen Seiten ─────────────────────────────────────────────────────

function basicsHtml(era: number): string {
  const data = balancing();
  const kette = chainOf(era);
  const ziel = kette[kette.length - 1];
  const heimat = era > data.eraFirst;
  const langsamer = Math.round((1 - data.emptySpeed) * 100);
  const seltener = Math.round((1 - data.emptyRate) * 100);
  // Aus `balancing()`, nicht getippt (Gate FG-31) — die Suchweite der ersten
  // Stufe ist der Grundwert, alles darueber kauft der Spieler.
  const bearingBase = Number(data.base.bearingRange);

  return head('Worum es geht') +
    para(heimat
      ? 'Der Ring hat gehalten. Du bist zu Hause — nur ist von zu Hause ein Trümmerfeld übrig, und ' +
        'niemand hat auf dich gewartet, weil niemand mehr da ist. Also baust du es wieder auf.'
      : 'Dein Sternentor wurde zerstört, kurz bevor du springen konntest. Die Rettungskapsel treibt ' +
        'allein in einem Asteroidenfeld — also baust du dir den Weg nach Hause selbst.') +
    para('Die Schleife ist immer dieselbe: <b>rausfliegen → abbauen → Erz sammeln → zur Kapsel ' +
      `zurückkehren → aufrüsten</b>. Am Ende steht ${heimat ? 'das' : 'der'} ` +
      `${escapeHtml(ziel.name)}.`) +

    head('Was dir nicht passieren kann') +
    para('Es gibt <b>keinen Feuerknopf</b> — der Laser schießt von selbst auf alles in Reichweite. ' +
      'Es gibt <b>keine Zielauswahl</b>, <b>kein Frachtlimit</b> und <b>keinen Tod</b>. Und nichts ' +
      'nimmt dir Fortschritt weg: Ressourcen sinken ausschließlich, wenn du etwas kaufst.') +

    head('Energie ist Reibung, keine Bestrafung') +
    para('Draußen sinkt die Energie langsam. Leer bedeutet <b>nicht</b> Stillstand, sondern zäh: rund ' +
      `${langsamer}% langsamer und ${seltener}% seltener Feuer — nie null. In der Ladezone um die ` +
      `Kapsel (${distStr(data.dockRange)}) füllt sie sich in Sekunden, und dort kostet Feuern gar nichts.`) +
    para('Auch weit draußen gibt es zwei Wege zurück zur vollen Zelle: die <b>Solarwind-Front</b> und ' +
      'jeder erledigte Gegner. Beide laden langsamer als die Kapsel — der Rückweg lohnt sich weiter.') +
    para('Die Front findet man sonst nur zufällig — draußen sieht das Feld in jeder Richtung gleich ' +
      `aus. Der Sensorik-Ast hat dafür die <b>Solarwind-Peilung</b>: einmal <b>F</b>, und ein Pfeil ` +
      'am Bildrand zeigt samt Entfernung auf die nächstgelegene Front, bis er nach ein paar Sekunden ' +
      `verblasst. Die erste Stufe reicht ${distStr(bearingBase)} weit, jede weitere vergrößert die ` +
      'Suchweite. Ist nichts in Reichweite, sagt sie das.') +

    head('Die Hülle') +
    para('Gegner beschädigen die Hülle. Bei null wirst du zur Kapsel geschleppt und repariert; das ' +
      `dauert ${timeStr(data.towSeconds)} und kostet <b>nie</b> Erz, Erfahrung oder eine gekaufte ` +
      `Stufe. Panzerung nimmt höchstens ${Math.round(data.armorCap * 100)}% des Schadens weg.`) +

    head('Der Rand des Feldes') +
    para(`Die Welt ist endlos, aber ab ${distStr(data.world.fieldRadius)} steht die <b>Alphawand</b>: ` +
      `ein sanfter Schub drückt dich über ${distStr(data.world.wallBand)} zurück. Sie bremst dich — ` +
      'sie beschädigt dich nicht. Das Feld dahinter ist dasselbe wie davor; begrenzt ist der Flug, ' +
      'nicht die Welt.') +
    para('Ist die Kapsel aus dem Bild, zeigt ein Pfeil am Bildrand in ihre Richtung. Die Welt sieht in ' +
      'jede Richtung gleich aus; ohne den Pfeil wäre der Rückweg Raten.');
}

function zonesHtml(progress: HelpProgress): string {
  const data = balancing();
  const fakten = zoneFactsAll(progress.deepest, progress.era);
  const karten = fakten.map(zoneCardHtml).join('');
  // Der äußerste Ring und der davor — beide aus der Tabelle, keine Zahl getippt
  // (Gate FG-31). Der Name richtet sich nach der Ära des Spielers.
  const tief = fakten[fakten.length - 1];
  const davor = fakten[fakten.length - 2];

  return head('Die Zonen') +
    para(`Das Feld ist in <b>${zonesFor(progress.era).length} konzentrische Ringe</b> um die Kapsel ` +
      'geteilt. Welche ' +
      'Zone gilt, hängt allein an der Luftlinie zur Kapsel — nicht an der Richtung. Je weiter draußen, ' +
      'desto härter die Brocken, desto wertvoller das Erz und desto rauer die Nachbarschaft — bis auf ' +
      'den äußersten Ring, der eigenen Regeln folgt.') +
    // Der Name steht als Beisatz, nicht hinter einem Artikel: „Der Tiefer Raum"
    // wäre falsch gebeugt, und der Name kommt aus der Tabelle. Dieselbe
    // Vorsicht beim Bezugspunkt — „der ersten Zone" statt eines Genitivs, den
    // ein Tabellenname nicht mitbringt.
    para(`Der äußerste Ring — <b>${escapeHtml(tief.name)}</b>, ab ${distStr(tief.from)} — ist diese ` +
      'Ausnahme: dort wird es <b>nicht weiter härter</b>. Die Härte steht schon davor beim ' +
      `${fmt(davor.hpMul)}-fachen der ersten Zone, und noch ein Sprung wäre keine Steigerung mehr, ` +
      'sondern eine Mauer. Stattdessen wird das Feld <b>dünner</b> — ' +
      `${Math.round((1 - tief.density / davor.density) * 100)}% weniger Brocken — und jeder einzelne ` +
      `trägt mehr: die Ausbeute steigt aufs ${fmt(tief.yieldMul)}-fache. Er reicht bis zur ` +
      'Alphawand, und er ist der Ort, an dem sich das Suchen lohnt.') +
    para('Jede Zone bringt <b>ein neues Erz</b> mit und behält eines aus der vorigen. Weiterfliegen ist ' +
      'deshalb nie ein Bruch, sondern eine Verschiebung: das alte Erz wird zur Beigabe, das neue zum ' +
      'Grund, überhaupt hier zu sein.') +
    para('Der Wechsel ist <b>hart, was die Regeln angeht</b>: an der Grenze springen Härte, Ausbeute, ' +
      'Erfahrung und Erztabelle um. Das <b>Bild</b> wechselt weich, und zwar dort, wo die Grenze in ' +
      'der Welt liegt — Himmel, Nebel und Sterne verlaufen auf den letzten Metern davor ineinander. ' +
      'Stehst du auf einer Grenze, siehst du beide Zonen im selben Bild: der Übergang zieht sich als ' +
      'Ring durch die Szene, und ein Brocken jenseits davon steht sichtbar in der anderen ' +
      'Nachbarschaft. Was du siehst, ist also schon die nächste Zone, was du abbaust, noch die alte.') +
    para(`Um die Kapsel bleibt es ruhig: Brocken wachsen erst ab ${distStr(data.world.clearRadius)}, ` +
      `Gegner, Anomalien und Sonden erst ab ${distStr(data.world.clearRadius * 2)}.`) +
    para(`Die Angaben „Härte", „Ausbeute" und „Erfahrung" sind <b>Vielfache der ersten Zone</b> ` +
      `(${escapeHtml(zonesFor(progress.era)[0].name)}). Die nackten Tabellenwerte sagen für sich ` +
      'nichts; interessant ist, um welchen Faktor sich beides ändert, wenn du eine Grenze ' +
      'überfliegst.') +
    karten;
}

function fieldHtml(era: number): string {
  const data = balancing();

  const klassen = [...data.rockKinds, data.richKind]
    .map((kind) => {
      const teile = kind.split > 0
        ? `zerspringt in ${anzahl(kind.split, 'Bruchstück', 'Bruchstücke')}`
        : 'zerspringt nicht';
      return row(kind.name, `${mul(kind.hp)}× Härte · ${mul(kind.yield)}× Ausbeute · ${teile}`);
    })
    .join('');

  const blasen = data.anomalies
    .map((kind) => {
      const wirkung: string[] = [];
      if (kind.regen > 0) wirkung.push(`lädt ${mul(kind.regen)} Energie je Sekunde`);
      if (kind.dragMul > 1) wirkung.push(`bremst um Faktor ${dec(kind.dragMul)}`);
      if (kind.gemLuckMul > 1) wirkung.push(`${dec(kind.gemLuckMul)}× Kristallchance`);
      if (kind.fireFree) wirkung.push('Feuern kostet keine Energie');
      return `<div class="hitem"><div class="hname">${escapeHtml(kind.name)} <span class="hnote">ab ` +
        `${escapeHtml(zoneName(kind.minZone, era))}</span></div>` +
        `<div class="hdesc">${escapeHtml(kind.desc)}</div>` +
        `<div class="hdesc">${escapeHtml(wirkung.join(' · '))}</div></div>`;
    })
    .join('');

  // Die Erze **dieser** Ära: welches Erz wo fällt, ist die häufigste Frage an
  // ein Nachschlagewerk — und nach dem Sprung ist die Antwort eine andere.
  const erze = oresOf(era)
    .map((id) => resource(id))
    .filter((res): res is Resource => res !== undefined)
    .map((res) => {
      const zonen = zonesOfOre(res.id, era).map((zone) => zone.name);
      return row(`${resIconSvg(res, 14)} ${escapeHtml(res.name)}`,
        `${res.gem ? 'Edelstein · ' : ''}fällt in: ${escapeHtml(zonen.join(', '))}`);
    })
    .join('');

  return head('Die Erze') +
    para('Ein Brocken trägt keine Beschriftung — was in ihm steckt, sagt allein seine <b>Farbe</b> und ' +
      'die Zeichnung seiner Adern. Welches Erz wo fällt, steht in der Tabelle seiner Zone.') +
    erze +
    para(`Dazu ${konverterErze().length === 1 ? 'kommt' : 'kommen'} ` +
      `${escapeHtml(konverterErzListe())}: sie ${konverterErze().length === 1 ? 'liegt' : 'liegen'} ` +
      `in <b>keiner</b> Zone und ${konverterErze().length === 1 ? 'entsteht' : 'entstehen'} nur ` +
      'im Ressourcenkonverter.') +

    head('Die Brocken') +
    para('Jeder Brocken hat eine <b>Klasse</b> und ein <b>Erz</b>. Die Klasse bestimmt Größe, Härte und ' +
      'Ausbeute, das Erz kommt aus der Tabelle seiner Zone. Beides wird mit den Zonenwerten ' +
      'multipliziert — derselbe „Riesige Brocken" ist in der äußersten Zone ungleich zäher als in der ' +
      'ersten.') +
    klassen +
    para(`Der <b>${escapeHtml(data.richKind.name)}</b> ersetzt in etwa ` +
      `${dec(data.world.richChance * 100)}% der Fälle die gezogene Klasse. Er ist härter, gibt aber ` +
      'deutlich mehr Erz und Erfahrung — und er sieht anders aus, du erkennst ihn also von weitem.') +
    para('Größere Brocken <b>zerspringen</b> in Bruchstücke, wenn sie fallen. Die tragen das Erz ihres ' +
      'Ursprungs weiter, nicht das der Zone — ein Bruchstück in der Nachbarzone bleibt bei seinem Erz.') +

    head('Nachwachsen') +
    para(`Ein abgebauter Brocken ist nach ${timeStr(data.world.respawnDelay)} wieder da. Das ist ein ` +
      'Balancing-Wert, kein Detail: käme er sofort zurück, gäbe es keinen Grund weiterzufliegen, und ' +
      '„rausfliegen" verlöre seinen Sinn. Weiterziehen lohnt sich, Dableiben aber auch.') +
    para('Das Feld selbst wird nie gespeichert und nie übertragen. Es wird aus seiner Saat berechnet — ' +
      'deshalb ist es endlos, und deshalb sieht es für alle Mitspieler gleich aus.') +

    head('Erz einsammeln') +
    para('Erz fliegt von selbst ins Frachtdeck, sobald es in Reichweite des Traktorfelds liegt. Im ' +
      '<b>Basis-Panel</b> kannst du mit dem Erzfilter einzelne Erze abschalten; Laser und Drohnen ' +
      'überspringen sie dann. Der letzte Haken bleibt stehen — ein Schiff, das nichts mehr abbaut, ' +
      'sähe wie ein Fehler aus.') +
    para('Daneben steht das <b>Wunscherz</b>: die Drohnen holen dann bevorzugt dieses eine, alles ' +
      'andere bleibt gleichrangig. Es ist eine <b>Neigung, keine Zielauswahl</b> — du wählst, ' +
      '<i>was</i> sie lieber holen, nicht <i>welchen</i> Brocken. Ist keins in Reichweite, graben ' +
      'sie weiter wie bisher: es legt sie nie still. Es gilt nur für die Drohnen; Laser, Raketen ' +
      'und Nova bleiben unberührt, und ein ausgefiltertes Erz kann keins sein.') +

    head('Anomalien') +
    para('Anomalien sind ortsfeste Blasen im Feld, genau so berechenbar wie die Brocken. Sie sind ' +
      '<b>Reibung oder Belohnung, nie Beschleunigung</b>. Ein Chip im HUD sagt dir, worin du steckst.') +
    blasen +

    head('Verlassene Sonden') +
    para(`Sehr selten liegt weit draußen ein Wrack. Bergen gibt Erfahrung und einen Eintrag im ` +
      `Missionslog — <b>nie Erz</b>. Es gibt ${data.probeLogs.length} Logs; jedes wird nur einmal voll ` +
      `belohnt, ein Wiederfund gibt noch ${Math.round(data.probes.repeatShare * 100)}%. Sonden liegen ` +
      `ab dem ${escapeHtml(zoneName(data.probes.minZone, era))}.`);
}

function enemiesHtml(era: number): string {
  const liste = enemiesOf(era)
    .map((foe) => {
      const bedingung = foe.minBuild > 0
        ? `ab ${escapeHtml(zoneName(foe.minZone, era))}, ${foe.minBuild} Ausbaustufen`
        : `ab ${escapeHtml(zoneName(foe.minZone, era))}`;
      const zusatz = foe.colossal ? ' · allein nicht zu schaffen' : '';
      return `<div class="hitem"><div class="hname">${escapeHtml(foe.name)} ` +
        `<span class="hnote">${bedingung}</span></div>` +
        `<div class="hdesc">${mul(foe.damage)} Schaden je Sekunde · Wachbereich ` +
        `${distStr(foe.reach)} · gibt ${mul(foe.energy)} Energie zurück${zusatz}</div></div>`;
    })
    .join('');

  return head('Gegner') +
    para('Gegner beschädigen dich, aber sie nehmen dir <b>nichts weg</b>: kein Erz, keine Erfahrung, ' +
      'keine gekaufte Stufe. Die Hülle ist die zweite Reibungsgröße neben der Energie — mehr nicht.') +

    head('Ein Gegner bewacht, er verfolgt nicht') +
    para('Jeder Gegner hat einen <b>Wachbereich</b> um seinen Standort. Diese eine Zahl ist zugleich ' +
      'Aufmerksamkeit, Leine und Feuerreichweite: innerhalb beschießt er dich, außerhalb lässt er dich ' +
      'in Ruhe. Wer nicht kämpfen will, fliegt außen herum — das ist immer eine gültige Antwort.') +
    para('Sein Anflug und seine Geschosse sind <b>Darstellung</b>. Der Schaden wird stetig verrechnet, ' +
      'nicht Treffer für Treffer; ein ausgewichenes Geschoss rettet dich also nicht.') +
    para('Dein Feuer nimmt Gegner <b>vor</b> den Brocken: sie belegen die Zielplätze des Lasers und der ' +
      'Drohnen, die Brocken bekommen den Rest. Das ist eine Rangfolge, keine Bedienung — es bleibt bei ' +
      '„kein Feuerknopf, keine Zielauswahl".') +
    para('Härte und Erfahrung eines Gegners werden mit den Werten seiner Zone multipliziert. Dieselbe ' +
      'Art ist weiter draußen also deutlich zäher — und deutlich lohnender.') +
    liste +
    para('Ein erledigter Gegner lässt das seltenste Erz seiner Zone fallen und gibt Energie zurück. ' +
      'Kämpfen ist damit nie nur Verlust.');
}

function progressHtml(era: number): string {
  const data = balancing();
  const kette = chainOf(era);
  const auftraege = questsOf(era);
  const heimat = era > data.eraFirst;

  const stufen = kette
    .map((build) => `<div class="hitem"><div class="hname">${escapeHtml(build.name)}</div>` +
      `<div class="hdesc">${escapeHtml(build.desc)}</div>` +
      `<div class="hdesc">${costText(build.cost)}</div>` +
      `<div class="hdesc">→ ${escapeHtml(build.grants)}</div></div>`)
    .join('');

  // Die Nebenaufträge **dieser** Ära — dieselbe Regel wie bei den Aufträgen:
  // eine Kette aus einer Welt, in der man nicht ist, wäre ein Spoiler mit
  // Fortschritt 0 %.
  const feldauftraege = data.sideQuests.filter((entry) => entry.minEra <= era);
  const feldliste = feldauftraege
    .map((entry) => row(entry.name, `${escapeHtml(entry.desc)} · ${entry.steps.length} Schritte · ` +
      `braucht ${escapeHtml(build(entry.needBuild)?.name ?? entry.needBuild)}`))
    .join('');

  const booster = data.boosters
    .map((entry) => row(entry.name, `${escapeHtml(entry.desc)} · ${timeStr(entry.dur)} · ` +
      `${fmt(entry.cost)} ${escapeHtml(waehrung())}`))
    .join('');

  return head('Der Technologiebaum') +
    para(`<b>${data.nodes.length} Knoten</b> auf dem Reiter „Upgrades". Benachbarte Knoten bleiben ` +
      'verdeckt, bis der Weg dorthin frei ist — ein Schloss heißt „noch nicht", nicht „nie". Die Farbe ' +
      'einer Kachel sagt ohne Klicken, was geht: warm heißt „hier ist noch etwas zu holen", kalt heißt ' +
      '„fertig ausgebaut".') +
    para('Halten kauft wiederholt. Mit dem Finger wählt der erste Tipp einen Knoten aus und zeigt ' +
      'Wirkung und Kosten, der zweite kauft.') +

    head(heimat ? 'Der Wiederaufbau' : 'Basis-Ausbau') +
    para(`<b>${kette.length} Stufen</b>, streng nacheinander. Jede setzt ein eigenes Bauteil an ` +
      'die Kapsel und öffnet einen ganzen Ast des Baums — der Ausbau ist der Takt, der Baum die ' +
      'Feinabstimmung. Nach dem Bezahlen kommt ein kurzes Schiebepuzzle; es entscheidet nichts, die ' +
      'Stufe steht schon.') +
    (heimat
      ? para('Was du im Randfeld gebaut hast, zählt weiter — es steht nur nicht mehr in dieser ' +
        'Liste. Die neue Kette fängt bewusst wieder mit Grundstoffen an: Trümmer räumt man mit dem, ' +
        'was man mitbringt.')
      : '') +
    stufen +

    head('Level und Punkte') +
    para(`Erfahrung gibt es für jeden zerlegten Brocken und jeden erledigten Gegner. Jeder Aufstieg ` +
      `bringt ${data.pointsPerLevel} Punkt für einen von ${data.perks.length} dauerhaften Boni — ` +
      'auszugeben auf dem Reiter „Punkte".') +

    head('Aufträge') +
    para(`<b>${auftraege.length} Aufträge</b> laufen nebenher und werden von deiner eigenen Statistik ` +
      'ausgelöst. Einige geben neben der Belohnung einen <b>dauerhaften</b> Bonus.') +
    (heimat
      ? para('Die Aufträge aus dem Randfeld bleiben gültig und abholbar — ein erfüllter Auftrag ' +
        'verfällt nicht, nur weil du weitergezogen bist.')
      : '') +

    head('Aufträge im Feld') +
    para(`<b>${feldauftraege.length} Nebenaufträge</b> stehen darunter — und sie lesen keinen ` +
      'Zähler, sondern einen <b>Ort</b>. Ein Frachtstück abholen, an mehreren Stellen etwas ' +
      'einsammeln, alles an der Kapsel abliefern: das passiert nicht nebenher, dafür fliegt man ' +
      'hin. Wo die Stellen liegen, ist in jedem Feld anders und wird aus der Weltsaat gerechnet — ' +
      'übertragen wird nichts.') +
    para('Jede Kette braucht ihre Ausbaustufe — ohne sie findet niemand einen Punkt im endlosen ' +
      'Feld, und ohne Kran hebt niemand eine Palette. Welche das jeweils ist, steht unten. Ein ' +
      'Nebenauftrag läuft nie ab und nimmt nie etwas — wer ihn liegen lässt, findet ihn ' +
      'unverändert vor.') +
    feldliste +

    head('Reisen') +
    para('Drei Stufen machen aus der endlosen Welt eine Strecke, die man planen kann. Die ' +
      '<b>Karte</b> (Taste K) zeigt Zonenringe, Kapsel, Schiff und jeden offenen Auftragsschritt; ' +
      'ein Tipp darauf setzt einen Kurs. Der <b>Autopilot</b> fliegt ihn — und jede Hand am Steuer ' +
      'bricht ihn ab. Der <b>Lichtflug</b> (Taste V) legt ein Feld um den Rumpf: nach kurzem ' +
      'Anlauf ein Vielfaches der Höchstgeschwindigkeit, dafür schweigen die Waffen und die Zelle ' +
      'leert sich. Reisen oder abbauen, nicht beides — und im Gefecht springt niemand davon.') +

    head('Booster') +
    para(`<b>${data.boosters.length} Booster</b>, bezahlt mit ${escapeHtml(waehrung())}. Sie sind die ` +
      'Senke für die Edelstein-Währung — kostenlos wären sie ein Dauerzustand statt einer Entscheidung. ' +
      'Der Reiter erscheint, sobald du den ersten Kristall hast.') +
    booster +

    head('Ressourcenkonverter') +
    para(`Ab dem Kristalllabor wandelt der Konverter Rohstoffe um, nach <b>${data.recipes.length} ` +
      'Rezepten</b>. Sie sind bewusst <b>verlustbehaftet</b>: ein gewinnbringendes Rezept wäre eine ' +
      `Abkürzung für Aufträge. Nur so ${konverterErze().length === 1 ? 'entsteht' : 'entstehen'} ` +
      `${escapeHtml(konverterErzListe())} für die letzten Stufen — nichts davon liegt in einer Zone, ` +
      'in keinem Akt.');
}

/**
 * Was der Konverter erzeugt — aus der Rezepttabelle, nicht getippt.
 *
 * Seit der Linsenkette sind es mehrere Erzeugnisse; die Liste bleibt in
 * Tabellenreihenfolge und dedupliziert, weil mehrere Rezepte denselben
 * Ausgang haben (`comp_*`).
 */
function konverterErze(): string[] {
  const gesehen = new Set<string>();
  const namen: string[] = [];
  for (const recipe of balancing().recipes) {
    if (gesehen.has(recipe.outRes)) continue;
    gesehen.add(recipe.outRes);
    const res = resource(recipe.outRes);
    namen.push(res ? res.name : recipe.outRes);
  }
  return namen;
}

/** „Verbundsubstanz, Fokuslinse und Resonanzlinse" — Aufzaehlung mit „und". */
function konverterErzListe(): string {
  const namen = konverterErze();
  if (namen.length <= 1) return namen[0] ?? '';
  return `${namen.slice(0, -1).join(', ')} und ${namen[namen.length - 1]}`;
}

/** Der Name der Booster-Waehrung, aus der Tabelle statt aus dem Gedaechtnis. */
function waehrung(): string {
  const res = resource(balancing().boosterCurrency);
  return res ? res.name : balancing().boosterCurrency;
}

function coopHtml(era: number): string {
  const data = balancing();
  const sektor = data.world.sectorChunks * data.world.chunk;

  const flotte = data.fleetOrders
    .map((order) => `<div class="hitem"><div class="hname">${escapeHtml(order.name)}</div>` +
      `<div class="hdesc">${escapeHtml(order.desc)}</div></div>`)
    .join('');

  return head('Dasselbe Feld für alle') +
    para('Im Online-Modus teilst du das Asteroidenfeld mit anderen. Ein abgebauter Brocken ist für alle ' +
      'weg und wächst für alle wieder nach. Das Feld selbst wird nie übertragen — beide Seiten rechnen ' +
      'es aus derselben Saat aus, über die Leitung geht nur, was davon abweicht.') +
    para('Der Modus ist <b>kooperativ</b>. Es gibt keinen Handel, kein Gegeneinander und kein ' +
      'Erstschlagsrecht auf einen Brocken. Wer neben dir gräbt, nimmt dir nichts.') +
    para('Eine <b>Bestenliste</b> gibt es trotzdem — wer am meisten abgebaut hat und wer am ' +
      'weitesten ist, für diese Woche und insgesamt. Sie zeigt nur an: Sie kostet nichts, ' +
      'schaltet nichts frei, und dein Platz nimmt niemandem etwas weg. Dein eigener Eintrag ' +
      'steht immer dabei, auch weit hinten.') +
    para(`Du siehst und hörst, was in deiner Nachbarschaft passiert — dein Sektor von ` +
      `${distStr(sektor)} Kantenlänge und seine acht Nachbarn. Der Rest des Feldes bleibt still.`) +
    para('<b>Ohne Verbindung läuft das Spiel weiter</b>, dann solo mit lokalem Zwischenspeicher. Ein ' +
      'Verbindungsabbruch ist der Normalfall, kein Fehler.') +

    head('Kolossbrocken') +
    para(`Ein Kolossbrocken hat zu viele Trefferpunkte für einen allein: das ` +
      `${mul(data.colossus.hpMul)}-Fache der Härte seiner Zone. Er steht ` +
      `${timeStr(data.colossus.duration)} und schüttet dann nach Schadensanteil aus — mit einem ` +
      `Mindestanteil von ${Math.round(data.colossus.minShare * 100)}%, damit Mitschleppen nichts ` +
      `bringt. Er erscheint ab dem ${escapeHtml(zoneName(data.colossus.minZone, era))}.`) +
    para('Er erzeugt trotzdem keinen Wettbewerb: mehr Schaden löst die Ausschüttung überhaupt erst aus. ' +
      'Der Anteil des einen verkleinert den des anderen nicht spürbar.') +

    head('Flottenaufträge') +
    para(`<b>${data.fleetOrders.length} Flottenaufträge</b> rotieren wöchentlich je Feld: ein ` +
      'gemeinsames Ziel, an dem alle mitsammeln. Ist es erfüllt, läuft ein zeitlich begrenzter ' +
      `Feld-Buff für <b>alle</b> — ${timeStr(data.fleetBuffDuration)} lang, auch für die, die wenig ` +
      'beigetragen haben.') +
    flotte +

    head('Signal') +
    para('Mit <b>X</b> setzt du ein Signal, das Mitspieler in der Nachbarschaft sehen. Es trägt keine ' +
      'Nachricht und zeigt nur auf dich — mehr braucht es nicht, um jemanden zu einem Kolossbrocken zu ' +
      'rufen.');
}

/**
 * Die Kolonie (Akt III) — **nur im Heimatsystem**.
 *
 * Davor ist sie eine Vorschau auf ein Spiel, das niemand gesehen hat; die
 * Seite sagt dann nur, dass es weitergeht. Jede Zahl kommt aus der Tabelle.
 */
function colonyHtml(era: number): string {
  const c = balancing().colony;
  if (era < c.minEra) {
    return head('Später') +
      para('Diese Seite erklärt einen Abschnitt, der erst später beginnt — nach dem Ende dieses Akts ' +
        'und nach dem, was danach kommt. Eine Vorschau wäre ein Spiel, das du noch nicht kennst. ' +
        'Sobald du dort bist, steht hier, was gilt: Orte, Anlagen, Projekte und Zahlen.');
  }
  const homefire = build('homefire')?.name ?? 'Heimatfeuer';
  const planetZeilen = planets().map((planet) => row(
    escapeHtml(planet.name),
    `${distStr(planet.dist)} von der Kapsel · ${anzahl(projectsOf(planet.id).length, 'Projekt', 'Projekte')}`,
  )).join('');
  const anlagen = colonyBuildings().map((kind) => row(
    escapeHtml(kind.name),
    kind.power > 0 ? `+${fmt(kind.power)} Energie je Stufe` : `${fmt(-kind.power)} Energie je Stufe`,
  )).join('');
  const roh = goods().filter((gut) => gut.extract > 0).map((gut) => escapeHtml(gut.name)).join(', ');

  return head('Planeten') +
    para(`Wenn das <b>${escapeHtml(homefire)}</b> brennt, liegen im Heimatsystem ` +
      `${anzahl(planets().length, 'Planet', 'Planeten')} — als Kugeln im Feld und als Rauten auf der ` +
      'Karte. Flieg in ihre Umlaufbahn, dann kannst du im Reiter „Kolonie" landen. Die Landung kostet ' +
      'Vorräte aus dem Laderaum, und der Preis steht vorher da.') +
    planetZeilen +
    head('Bauen') +
    para(`Die Oberfläche ist ein Raster aus ${c.gridW} × ${c.gridH} Kacheln. Erschlossen ist zuerst ` +
      `ein Ring von ${c.startRadius} Kacheln um die Kapsel; Projekte weiten ihn. Fördertürme brauchen ` +
      `ein Vorkommen — ${roh} —, ein Geothermiekraftwerk eine ${escapeHtml(c.depositHeatName)}. ` +
      `Jede weitere Anlage derselben Art kostet ${mul(c.placeGrowth)} so viel wie die vorige.`) +
    para('<b>Erster Tipp wählt, zweiter handelt</b> — außer mit einem Bauplan in der Hand: dann baut der ' +
      'Tipp sofort. Eine Anlage lässt sich ausbauen, anhalten und kostenlos versetzen; abgerissen ' +
      'wird nichts, denn ein Abriss nähme etwas weg, wofür du bezahlt hast.') +
    anlagen +
    head('Produktion') +
    para('Jede Anlage braucht Energie. Reicht das Netz nicht, laufen <b>alle</b> Verbraucher ' +
      'gleichmäßig langsamer — nichts steht still. Fehlt einer Halle ein Eingang, läuft sie nur ' +
      'so weit, wie er reicht, und ihre Kachel trägt eine rote Marke.') +
    para(`Die Kolonie arbeitet, <b>solange du spielst</b> — auch während du im Feld fliegst. Wer das ` +
      `Spiel schließt, bekommt beim nächsten Mal höchstens ${timeStr(c.gapMax)} gutgeschrieben, nicht ` +
      'die ganze Pause. Ein Inkrementalspiel, das ohne dich weiterwächst, belohnte das Wegbleiben.') +
    head('Projekte') +
    para(`${anzahl(projects().length, 'Projekt', 'Projekte')} auf allen Planeten, je Planet streng ` +
      'der Reihe nach. Sie weiten das Gebiet, heben die Leistung einer Anlagenart, schalten neue ' +
      'Anlagen frei, öffnen den Flug zum nächsten Planeten — und einige verbessern dein Schiff. ' +
      'Das letzte vollendet Akt III.');
}

function controlsHtml(): string {
  const tasten = KEY_BINDINGS
    .map((binding) => `<span>${escapeHtml(binding.keys)}</span><span>${escapeHtml(binding.action)}</span>`)
    .join('');
  const gesten = TOUCH_HINTS
    .map(([geste, wirkung]) => `<span>${escapeHtml(geste)}</span><span>${escapeHtml(wirkung)}</span>`)
    .join('');

  return head('Tastatur und Maus') +
    `<div class="keys">${tasten}</div>` +
    para('Der Zoom bleibt eine <b>ganze Zahl</b> — das Bild ist pixelig, nicht unscharf. Die gewählte ' +
      'Stufe merkt sich dieser Browser; sie gehört nicht zum Spielstand.') +

    head('Mit dem Finger') +
    `<div class="keys">${gesten}</div>` +

    head('Konto') +
    para('Der Einstieg ist ein <b>Gastkonto</b>: kein Formular, kein Passwort, sofort spielbar. Der ' +
      'Preis liegt offen — der Zugang hängt an diesem Browser. Über den Chip oben rechts sicherst du ' +
      'das Konto mit Namen und Passwort, <b>ohne</b> Fortschritt zu verlieren: es ist dasselbe Konto, ' +
      'nur nicht mehr anonym.') +
    para('Online liegt der Spielstand beim Server, und dann gilt er. Solo- und Online-Stand werden ' +
      'bewusst <b>nicht</b> verschmolzen — zwei Historien zusammenzuführen erzeugt Fälle, die niemand ' +
      'testet, und der Verlust fiele erst später auf.') +

    head('Spielstand') +
    para('Ein alter oder beschädigter Spielstand bringt das Spiel nie zum Absturz — im Zweifel beginnt ' +
      'er leer. Die Zusage lautet nicht „dein Stand überlebt jede Version", sondern „er stürzt nie ab".') +
    `<p class="hp"><button ${WIPE_ATTR}="1" class="dim">Spielstand löschen</button></p>`;
}

// ── Zusammenbau ──────────────────────────────────────────────────────────────

export function helpTabsHtml(activeId: string): string {
  return HELP_PAGES
    .map((page) => `<button class="subtab ${page.id === activeId ? 'active' : ''}" ` +
      `data-help="${page.id}">${escapeHtml(page.title)}</button>`)
    .join('');
}

/**
 * Der Koerper einer Hilfeseite.
 *
 * Eine unbekannte ID faellt auf die erste Seite zurueck statt zu werfen: die ID
 * kommt aus einem `data`-Attribut, und ein Tippfehler dort soll die Hilfe nicht
 * unbenutzbar machen.
 */
export function helpPageHtml(pageId: string, progress: HelpProgress): string {
  switch (pageId) {
    case 'zones': return zonesHtml(progress);
    case 'field': return fieldHtml(progress.era);
    case 'enemies': return enemiesHtml(progress.era);
    case 'progress': return progressHtml(progress.era);
    case 'coop': return coopHtml(progress.era);
    case 'colony': return colonyHtml(progress.era);
    case 'controls': return controlsHtml();
    default: return basicsHtml(progress.era);
  }
}
